import React, { useEffect, useState } from 'react';
import api from '../services/api';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    ReferenceLine, Brush, BarChart, Bar, Cell
} from 'recharts';
import {
    Box,
    Typography,
    Container,
    Paper,
    ToggleButtonGroup,
    ToggleButton,
    Card,
    CardContent,
    Grid,
    Chip,
    CircularProgress,
    Fade,
    MenuItem,
    Select,
    FormControl,
    FormControlLabel,
    InputLabel,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tab
} from '@mui/material';
import {
    TrendingUp,
    TrendingDown,
    CalendarMonth,
    CalendarToday,
    ShowChart,
    BarChart as BarChartIcon,
    Business,
    AccountBalance,
    Receipt
} from '@mui/icons-material';

function CustomDot({ cx, cy, value }) {
    return (
        <circle 
            cx={cx} 
            cy={cy} 
            r={6} 
            fill={value >= 0 ? '#11998e' : '#ee0979'} 
            stroke="white" 
            strokeWidth={2} 
        />
    );
}

function ProfitLossChart() {
    const [chartData, setChartData] = useState([]);
    const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'yearly'
    const [chartType, setChartType] = useState('line'); // 'line' or 'bar'
    const [selectedYear, setSelectedYear] = useState('all');
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0); // 0 = Overview, 1 = Business Breakdown
    const [businessYear, setBusinessYear] = useState(new Date().getFullYear());
    const [businessTransactions, setBusinessTransactions] = useState([]);
    const [businessLoading, setBusinessLoading] = useState(false);
    const [includePersonal, setIncludePersonal] = useState(true);

    const isBusinessTransaction = (tx) => {
        const type = tx.classification || tx.tag?.type || '';
        return type.toLowerCase() !== 'personal';
    };

    const buildExpensesByCategoryMoM = (transactions, includePersonalTx = true) => {
        const filtered = transactions.filter(tx => {
            if (tx.type?.toLowerCase() !== 'expense') return false;
            return includePersonalTx || isBusinessTransaction(tx);
        });
        const byMonth = {};
        filtered.forEach(tx => {
            const d = new Date(tx.date);
            const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const monthLabel = d.toLocaleString('default', { month: 'short', year: 'numeric' });
            const category = tx.tag?.tag || 'Unknown';
            if (!byMonth[monthKey]) byMonth[monthKey] = { monthKey, monthLabel, total: 0 };
            byMonth[monthKey].total += Math.abs(tx.amount || 0);
            byMonth[monthKey][category] = (byMonth[monthKey][category] || 0) + Math.abs(tx.amount || 0);
        });
        return Object.values(byMonth).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
    };

    const buildDepositsBySourceMoM = (transactions, includePersonalTx = true) => {
        const filtered = transactions.filter(tx => {
            if (tx.type?.toLowerCase() !== 'deposit') return false;
            return includePersonalTx || isBusinessTransaction(tx);
        });
        const byMonth = {};
        filtered.forEach(tx => {
            const d = new Date(tx.date);
            const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const monthLabel = d.toLocaleString('default', { month: 'short', year: 'numeric' });
            const source = tx.tag?.tag || 'Unknown';
            if (!byMonth[monthKey]) byMonth[monthKey] = { monthKey, monthLabel, total: 0 };
            byMonth[monthKey].total += Math.abs(tx.amount || 0);
            byMonth[monthKey][source] = (byMonth[monthKey][source] || 0) + Math.abs(tx.amount || 0);
        });
        return Object.values(byMonth).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
    };

    const getTopCategories = (data, topN = 10, excludeKeys = ['monthKey', 'monthLabel', 'total']) => {
        const totals = {};
        data.forEach(row => {
            Object.keys(row).filter(k => !excludeKeys.includes(k)).forEach(k => {
                totals[k] = (totals[k] || 0) + (row[k] || 0);
            });
        });
        return Object.entries(totals)
            .sort((a, b) => b[1] - a[1])
            .slice(0, topN)
            .map(([k]) => k);
    };

    useEffect(() => {
        setLoading(true);
        api.get(`/api/csv/summary`)
            .then(response => {
                let data = response.data;
                
                // Normalize: ensure year is set (extract from month string if null)
                const extractYear = (item) => {
                    if (item.year != null && !isNaN(parseInt(item.year))) return parseInt(item.year);
                    const monthStr = item.month ? item.month.toString() : '';
                    const match = monthStr.match(/\b(19|20)\d{2}\b/);
                    return match ? parseInt(match[0]) : null;
                };

                data = data.map((item) => {
                    let displayMonth = (item.month || '').trim();
                    let year = item.year != null ? parseInt(item.year) : extractYear(item);
                    if (year == null || isNaN(year)) year = extractYear(item);

                    if (year != null && !displayMonth.match(/\b(19|20)\d{2}\b/)) {
                        displayMonth = displayMonth ? `${displayMonth} ${year}` : String(year);
                    }

                    if (displayMonth && !displayMonth.match(/^\d{4}$/)) {
                        const parts = displayMonth.split(' ');
                        displayMonth = parts.map((p, i) => 
                            /\b(19|20)\d{2}\b/.test(p) ? p : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()
                        ).join(' ');
                    }

                    return {
                        ...item,
                        year: year ?? item.year,
                        month: displayMonth
                    };
                });

                // Sort data by year and month (year is normalized above)
                data.sort((a, b) => {
                    try {
                        const yearA = a.year != null ? parseInt(a.year) : parseInt((a.month || '').match(/\b(19|20)\d{2}\b/)?.[0]);
                        const yearB = b.year != null ? parseInt(b.year) : parseInt((b.month || '').match(/\b(19|20)\d{2}\b/)?.[0]);
                        if (yearA !== yearB) return (yearA || 0) - (yearB || 0);

                        const monthOrder = ['january', 'february', 'march', 'april', 'may', 'june',
                            'july', 'august', 'september', 'october', 'november', 'december'];
                        const monthA = (a.month || '').toLowerCase().split(' ')[0];
                        const monthB = (b.month || '').toLowerCase().split(' ')[0];
                        return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
                    } catch (error) {
                        return 0;
                    }
                });
                
                setChartData(data);
            })
            .catch(error => {
                console.error('Error fetching data:', error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (activeTab !== 1) return;
        setBusinessLoading(true);
        const start = `${businessYear}-01-01`;
        const end = `${businessYear}-12-31`;
        api.get(`/api/csv/expenses`, { params: { startDate: start, endDate: end } })
            .then(response => setBusinessTransactions(response.data || []))
            .catch(err => {
                console.error('Error fetching business transactions:', err);
                setBusinessTransactions([]);
            })
            .finally(() => setBusinessLoading(false));
    }, [activeTab, businessYear]);

    const expensesByCategoryData = buildExpensesByCategoryMoM(businessTransactions, includePersonal);
    const depositsBySourceData = buildDepositsBySourceMoM(businessTransactions, includePersonal);
    const expenseCategories = getTopCategories(expensesByCategoryData);
    const depositSources = getTopCategories(depositsBySourceData);

    const yearlyTotalDeposits = depositsBySourceData.reduce((s, r) => s + (r.total || 0), 0);
    const yearlyTotalExpenses = expensesByCategoryData.reduce((s, r) => s + (r.total || 0), 0);
    const yearlyNetProfit = yearlyTotalDeposits - yearlyTotalExpenses;
    const yearlyProfitPercent = yearlyTotalDeposits > 0
        ? (yearlyNetProfit / yearlyTotalDeposits) * 100
        : null;

    const getYearlyData = () => {
        const yearlyMap = {};
        
        chartData.forEach(item => {
            try {
                let year;
                
                // Method 1: Check if there's a separate 'year' field
                if (item.year) {
                    year = parseInt(item.year);
                }
                
                // Method 2: Try direct date parsing
                if (!year || isNaN(year)) {
                    const date = new Date(item.month);
                    year = date.getFullYear();
                }
                
                // Method 3: Try regex extraction from month string
                if (!year || isNaN(year) || year < 1900 || year >= 2100) {
                    const monthStr = item.month ? item.month.toString() : '';
                    const matches = monthStr.match(/\b(19|20)\d{2}\b/);
                    if (matches) {
                        year = parseInt(matches[0]);
                    }
                }
                
                // Only process valid years
                if (!isNaN(year) && year > 1900 && year < 2100) {
                    const yearStr = year.toString();
                    
                    if (!yearlyMap[yearStr]) {
                        yearlyMap[yearStr] = {
                            year: yearStr,
                            totalDeposits: 0,
                            totalExpenses: 0,
                            profitOrLoss: 0
                        };
                    }
                    
                    yearlyMap[yearStr].totalDeposits += item.totalDeposits || 0;
                    yearlyMap[yearStr].totalExpenses += item.totalExpenses || 0;
                    yearlyMap[yearStr].profitOrLoss += item.profitOrLoss || 0;
                }
            } catch (error) {
                console.error('Error processing date for yearly data:', item.month, error);
            }
        });

        return Object.values(yearlyMap).sort((a, b) => parseInt(a.year) - parseInt(b.year));
    };

    const getFilteredMonthlyData = () => {
        if (selectedYear === 'all') {
            return chartData;
        }
        
        return chartData.filter(item => {
            try {
                let year;
                
                // Method 1: Check if there's a separate 'year' field
                if (item.year) {
                    year = parseInt(item.year);
                }
                
                // Method 2: Try direct date parsing
                if (!year || isNaN(year)) {
                    const date = new Date(item.month);
                    year = date.getFullYear();
                }
                
                // Method 3: Try regex extraction from month string
                if (!year || isNaN(year) || year < 1900 || year >= 2100) {
                    const monthStr = item.month ? item.month.toString() : '';
                    const matches = monthStr.match(/\b(19|20)\d{2}\b/);
                    if (matches) {
                        year = parseInt(matches[0]);
                    }
                }
                
                if (!isNaN(year) && year > 1900 && year < 2100) {
                    return year.toString() === selectedYear;
                }
                return false;
            } catch (error) {
                console.error('Error filtering by year:', item.month, error);
                return false;
            }
        });
    };

    const getAvailableYears = () => {
        const years = new Set();

        chartData.forEach((item) => {
            try {
                let year;
                
                // Method 1: Check if there's a separate 'year' field (most reliable)
                if (item.year) {
                    year = parseInt(item.year);
                }
                
                // Method 2: Try direct date parsing from month field
                if (!year || isNaN(year)) {
                    const date = new Date(item.month);
                    year = date.getFullYear();
                }
                
                // Method 3: Try extracting year from month string (e.g., "April 2025")
                if (!year || isNaN(year) || year < 1900 || year >= 2100) {
                    const monthStr = item.month ? item.month.toString() : '';
                    const matches = monthStr.match(/\b(19|20)\d{2}\b/);
                    if (matches) {
                        year = parseInt(matches[0]);
                    }
                }
                
                // If year is valid
                if (!isNaN(year) && year > 1900 && year < 2100) {
                    years.add(year.toString());
                }
            } catch (error) {
                console.error('Error parsing date:', item.month, error);
            }
        });

        return Array.from(years).sort();
    };

    const displayData = viewMode === 'yearly' ? getYearlyData() : getFilteredMonthlyData();
    const dataKey = viewMode === 'yearly' ? 'year' : 'month';

    const average = displayData.length > 0
        ? displayData.reduce((sum, item) => sum + item.profitOrLoss, 0) / displayData.length
        : 0;

    const totalProfit = displayData.reduce((sum, item) => {
        return sum + (item.profitOrLoss > 0 ? item.profitOrLoss : 0);
    }, 0);

    const totalLoss = displayData.reduce((sum, item) => {
        return sum + (item.profitOrLoss < 0 ? Math.abs(item.profitOrLoss) : 0);
    }, 0);

    const netProfitOrLoss = displayData.reduce((sum, item) => sum + item.profitOrLoss, 0);

    const StatCard = ({ title, value, icon, gradient, isNegative = false }) => (
        <Card
            sx={{
                background: gradient,
                color: 'white',
                height: '100%',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                            {title}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {isNegative && value > 0 ? '-' : ''}${Math.abs(value).toFixed(2)}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            borderRadius: '12px',
                            p: 1.5
                        }}
                    >
                        {icon}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );

    if (loading) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            py: 4
        }}>
            <Container maxWidth="xl">
                {/* Header */}
                <Fade in timeout={800}>
                    <Paper
                        elevation={0}
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            borderRadius: 4,
                            p: 4,
                            mb: 4
                        }}
                    >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <ShowChart sx={{ fontSize: 40, mr: 2 }} />
                                    <Typography variant="h3" fontWeight={700}>
                                        Analytics Dashboard
                                    </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                    Track your profit and loss trends over time
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Fade>

                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        mb: 4,
                        overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                    }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        sx={{
                            px: 2,
                            backgroundColor: '#f8f9fa',
                            '& .MuiTab-root': { fontWeight: 600, fontSize: '0.95rem', py: 2 },
                            '& .Mui-selected': { color: '#667eea' },
                            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' }
                        }}
                    >
                        <Tab icon={<ShowChart sx={{ mr: 1 }} />} iconPosition="start" label="Profit & Loss Overview" />
                        <Tab icon={<Business sx={{ mr: 1 }} />} iconPosition="start" label="Business Breakdown" />
                    </Tabs>
                </Paper>

                {activeTab === 1 ? (
                    <Fade in timeout={400}>
                        <Box>
                            {/* Business tab header */}
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    p: 3,
                                    mb: 4,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid rgba(0,0,0,0.06)'
                                }}
                            >
                                <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={2} mb={3}>
                                    <Box>
                                        <Typography variant="h5" fontWeight={700} color="text.primary" gutterBottom>
                                            Expense & Deposit Analytics
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Expenses by category and deposits by source, month over month.
                                        </Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={includePersonal}
                                                    onChange={(e) => setIncludePersonal(e.target.checked)}
                                                    size="small"
                                                />
                                            }
                                            label="Include Personal"
                                        />
                                        <FormControl size="small" sx={{ minWidth: 100 }}>
                                        <InputLabel>Year</InputLabel>
                                        <Select
                                            value={businessYear}
                                            label="Year"
                                            onChange={(e) => setBusinessYear(Number(e.target.value))}
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {[businessYear, businessYear - 1, businessYear - 2, businessYear - 3].map(y => (
                                                <MenuItem key={y} value={y}>{y}</MenuItem>
                                            ))}
                                        </Select>
                                        </FormControl>
                                    </Box>
                                </Box>

                                {!businessLoading && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                            mt: 2,
                                            pt: 3,
                                            borderTop: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <Box sx={{ flex: '1 1 180px', minWidth: 160, p: 2, borderRadius: 2, bgcolor: '#e8f5e9', border: '1px solid #a5d6a7' }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Total Deposits</Typography>
                                            <Typography variant="h6" fontWeight={700} color="#2e7d32">
                                                ${yearlyTotalDeposits.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: '1 1 180px', minWidth: 160, p: 2, borderRadius: 2, bgcolor: '#ffebee', border: '1px solid #ef9a9a' }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Total Expenses</Typography>
                                            <Typography variant="h6" fontWeight={700} color="#c62828">
                                                ${yearlyTotalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: '1 1 180px', minWidth: 180, p: 2, borderRadius: 2, bgcolor: yearlyNetProfit >= 0 ? '#e3f2fd' : '#fce4ec', border: yearlyNetProfit >= 0 ? '1px solid #90caf9' : '1px solid #f48fb1' }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Yearly Profit Margin</Typography>
                                            <Typography variant="h6" fontWeight={700} color={yearlyNetProfit >= 0 ? '#1565c0' : '#c2185b'}>
                                                {yearlyProfitPercent != null
                                                    ? `${yearlyProfitPercent >= 0 ? '+' : ''}${yearlyProfitPercent.toFixed(1)}%`
                                                    : 'N/A'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Net: ${yearlyNetProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Paper>

                            {businessLoading ? (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: 3,
                                        p: 8,
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}
                                >
                                    <CircularProgress size={48} sx={{ color: '#667eea' }} />
                                </Paper>
                            ) : (
                                <Grid container spacing={4}>
                                    {/* Expenses by Category */}
                                    <Grid item xs={12}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                borderRadius: 3,
                                                p: 4,
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                                border: '1px solid rgba(0,0,0,0.06)',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                                                <Box
                                                    sx={{
                                                        p: 1,
                                                        borderRadius: 2,
                                                        background: 'linear-gradient(135deg, rgba(238,9,121,0.15) 0%, rgba(255,106,0,0.15) 100%)'
                                                    }}
                                                >
                                                    <Receipt sx={{ color: '#ee0979', fontSize: 28 }} />
                                                </Box>
                                                <Box>
                                                    <Typography variant="h6" fontWeight={700} color="text.primary">
                                                        Expenses by Category
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Month over month breakdown
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {expensesByCategoryData.length > 0 && expenseCategories.length > 0 ? (
                                                <TableContainer sx={{ maxHeight: 480 }}>
                                                    <Table stickyHeader size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Month</TableCell>
                                                                {expenseCategories.map(cat => (
                                                                    <TableCell key={cat} align="right" sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>{cat}</TableCell>
                                                                ))}
                                                                <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Total</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {expensesByCategoryData.map(row => (
                                                                <TableRow key={row.monthKey} hover sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                                                                    <TableCell sx={{ fontWeight: 500 }}>{row.monthLabel}</TableCell>
                                                                    {expenseCategories.map(cat => (
                                                                        <TableCell key={cat} align="right">
                                                                            {(row[cat] || 0) > 0 ? `$${Number(row[cat]).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}
                                                                        </TableCell>
                                                                    ))}
                                                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#ee0979' }}>
                                                                        ${Number(row.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                                                                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                                                {expenseCategories.map(cat => {
                                                                    const sum = expensesByCategoryData.reduce((s, r) => s + (r[cat] || 0), 0);
                                                                    return (
                                                                        <TableCell key={cat} align="right" sx={{ fontWeight: 600 }}>
                                                                            {sum > 0 ? `$${sum.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}
                                                                        </TableCell>
                                                                    );
                                                                })}
                                                                <TableCell align="right" sx={{ fontWeight: 700, color: '#ee0979' }}>
                                                                    ${expensesByCategoryData.reduce((s, r) => s + (r.total || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            ) : (
                                                <Box py={8} textAlign="center">
                                                    <Receipt sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                                    <Typography color="text.secondary" fontWeight={500}>
                                                        No expense data for {businessYear}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                        {includePersonal ? 'Tag transactions as Business or Personal to see them here' : 'Include Personal to see all transactions'}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Paper>
                                    </Grid>

                                    {/* Deposits by Source */}
                                    <Grid item xs={12}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                borderRadius: 3,
                                                p: 4,
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                                border: '1px solid rgba(0,0,0,0.06)',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                                                <Box
                                                    sx={{
                                                        p: 1,
                                                        borderRadius: 2,
                                                        background: 'linear-gradient(135deg, rgba(17,153,142,0.15) 0%, rgba(56,239,125,0.15) 100%)'
                                                    }}
                                                >
                                                    <AccountBalance sx={{ color: '#11998e', fontSize: 28 }} />
                                                </Box>
                                                <Box>
                                                    <Typography variant="h6" fontWeight={700} color="text.primary">
                                                        Deposits by Source
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Month over month breakdown
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {depositsBySourceData.length > 0 && depositSources.length > 0 ? (
                                                <TableContainer sx={{ maxHeight: 480 }}>
                                                    <Table stickyHeader size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Month</TableCell>
                                                                {depositSources.map(src => (
                                                                    <TableCell key={src} align="right" sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>{src}</TableCell>
                                                                ))}
                                                                <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Total</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {depositsBySourceData.map(row => (
                                                                <TableRow key={row.monthKey} hover sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                                                                    <TableCell sx={{ fontWeight: 500 }}>{row.monthLabel}</TableCell>
                                                                    {depositSources.map(src => (
                                                                        <TableCell key={src} align="right">
                                                                            {(row[src] || 0) > 0 ? `$${Number(row[src]).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}
                                                                        </TableCell>
                                                                    ))}
                                                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#11998e' }}>
                                                                        ${Number(row.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                                                                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                                                {depositSources.map(src => {
                                                                    const sum = depositsBySourceData.reduce((s, r) => s + (r[src] || 0), 0);
                                                                    return (
                                                                        <TableCell key={src} align="right" sx={{ fontWeight: 600 }}>
                                                                            {sum > 0 ? `$${sum.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}
                                                                        </TableCell>
                                                                    );
                                                                })}
                                                                <TableCell align="right" sx={{ fontWeight: 700, color: '#11998e' }}>
                                                                    ${depositsBySourceData.reduce((s, r) => s + (r.total || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            ) : (
                                                <Box py={8} textAlign="center">
                                                    <AccountBalance sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                                    <Typography color="text.secondary" fontWeight={500}>
                                                        No deposit data for {businessYear}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                        {includePersonal ? 'Tag transactions as Business or Personal to see them here' : 'Include Personal to see all transactions'}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Paper>
                                    </Grid>
                                </Grid>
                            )}
                        </Box>
                    </Fade>
                ) : (
                    <>
                {/* Controls */}
                <Fade in timeout={1000}>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 3,
                            p: 3,
                            mb: 4,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                        }}
                    >
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    View Mode
                                </Typography>
                                <ToggleButtonGroup
                                    value={viewMode}
                                    exclusive
                                    onChange={(e, newMode) => newMode && setViewMode(newMode)}
                                    fullWidth
                                    sx={{
                                        '& .MuiToggleButton-root': {
                                            py: 1.5,
                                            fontWeight: 600
                                        }
                                    }}
                                >
                                    <ToggleButton value="monthly">
                                        <CalendarToday sx={{ mr: 1, fontSize: 18 }} />
                                        Monthly
                                    </ToggleButton>
                                    <ToggleButton value="yearly">
                                        <CalendarMonth sx={{ mr: 1, fontSize: 18 }} />
                                        Yearly
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    Chart Type
                                </Typography>
                                <ToggleButtonGroup
                                    value={chartType}
                                    exclusive
                                    onChange={(e, newType) => newType && setChartType(newType)}
                                    fullWidth
                                    sx={{
                                        '& .MuiToggleButton-root': {
                                            py: 1.5,
                                            fontWeight: 600
                                        }
                                    }}
                                >
                                    <ToggleButton value="line">
                                        <ShowChart sx={{ mr: 1, fontSize: 18 }} />
                                        Line
                                    </ToggleButton>
                                    <ToggleButton value="bar">
                                        <BarChartIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Bar
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>

                            {viewMode === 'monthly' && (
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Filter by Year</InputLabel>
                                        <Select
                                            value={selectedYear}
                                            label="Filter by Year"
                                            onChange={(e) => setSelectedYear(e.target.value)}
                                        >
                                            <MenuItem value="all">All Years</MenuItem>
                                            {getAvailableYears().map(year => (
                                                <MenuItem key={year} value={year}>{year}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Fade>

                {/* Statistics Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={4}>
                        <StatCard
                            title="Total Profit"
                            value={totalProfit}
                            icon={<TrendingUp sx={{ fontSize: 40 }} />}
                            gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard
                            title="Total Loss"
                            value={totalLoss}
                            icon={<TrendingDown sx={{ fontSize: 40 }} />}
                            gradient="linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)"
                            isNegative={true}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard
                            title={netProfitOrLoss >= 0 ? "Net Profit" : "Net Loss"}
                            value={netProfitOrLoss}
                            icon={netProfitOrLoss >= 0 ? 
                                <TrendingUp sx={{ fontSize: 40 }} /> : 
                                <TrendingDown sx={{ fontSize: 40 }} />
                            }
                            gradient={netProfitOrLoss >= 0 ?
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" :
                                "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                            }
                        />
                    </Grid>
                </Grid>

                {/* Chart */}
                <Fade in timeout={1200}>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 3,
                            p: 4,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                        }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h5" fontWeight={600}>
                                Profit & Loss Trend
                            </Typography>
                            <Chip
                                label={`Avg: $${average.toFixed(2)}`}
                                sx={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.9rem'
                                }}
                            />
                        </Box>

                        {displayData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={500}>
                                {chartType === 'line' ? (
                                    <LineChart data={displayData}>
                                        <defs>
                                            <linearGradient id="colorProfitLoss" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#667eea" stopOpacity={0.8} />
                                                <stop offset="95%" stopColor="#667eea" stopOpacity={0.1} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid stroke="#e0e0e0" strokeDasharray="5 5" />
                                        <XAxis 
                                            dataKey={dataKey} 
                                            tick={{ fill: '#333', fontWeight: viewMode === 'yearly' ? 'bold' : 'normal' }}
                                            style={{ fontSize: viewMode === 'yearly' ? '14px' : '12px' }}
                                        />
                                        <YAxis 
                                            tick={{ fill: '#666' }}
                                            style={{ fontSize: '12px' }}
                                        />
                                        <Tooltip
                                            formatter={(value) => [`$${value.toFixed(2)}`, 'Profit/Loss']}
                                            labelFormatter={(label) => viewMode === 'yearly' ? `Year: ${label}` : `Month: ${label}`}
                                            contentStyle={{
                                                backgroundColor: 'rgba(255,255,255,0.95)',
                                                borderRadius: '8px',
                                                border: 'none',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="profitOrLoss"
                                            stroke="#667eea"
                                            strokeWidth={3}
                                            fill="url(#colorProfitLoss)"
                                            dot={<CustomDot />}
                                            activeDot={{ r: 8 }}
                                            animationDuration={1500}
                                        />
                                        <ReferenceLine 
                                            y={average} 
                                            label={{ value: `Avg: $${average.toFixed(2)}`, fill: '#ee0979' }} 
                                            stroke="#ee0979" 
                                            strokeDasharray="3 3" 
                                        />
                                        <Brush 
                                            dataKey={dataKey} 
                                            height={30} 
                                            stroke="#667eea" 
                                        />
                                    </LineChart>
                                ) : (
                                    <BarChart data={displayData}>
                                        <CartesianGrid stroke="#e0e0e0" strokeDasharray="5 5" />
                                        <XAxis 
                                            dataKey={dataKey} 
                                            tick={{ fill: '#333', fontWeight: viewMode === 'yearly' ? 'bold' : 'normal' }}
                                            style={{ fontSize: viewMode === 'yearly' ? '14px' : '12px' }}
                                        />
                                        <YAxis 
                                            tick={{ fill: '#666' }}
                                            style={{ fontSize: '12px' }}
                                        />
                                        <Tooltip
                                            formatter={(value) => [`$${value.toFixed(2)}`, 'Profit/Loss']}
                                            labelFormatter={(label) => viewMode === 'yearly' ? `Year: ${label}` : `Month: ${label}`}
                                            contentStyle={{
                                                backgroundColor: 'rgba(255,255,255,0.95)',
                                                borderRadius: '8px',
                                                border: 'none',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                        <Legend />
                                        <Bar 
                                            dataKey="profitOrLoss" 
                                            radius={[8, 8, 0, 0]}
                                        >
                                            {displayData.map((entry, index) => (
                                                <Cell 
                                                    key={`cell-${index}`} 
                                                    fill={entry.profitOrLoss >= 0 ? '#11998e' : '#ee0979'} 
                                                />
                                            ))}
                                        </Bar>
                                        <ReferenceLine 
                                            y={average} 
                                            label={{ value: `Avg: $${average.toFixed(2)}`, fill: '#764ba2' }} 
                                            stroke="#764ba2" 
                                            strokeDasharray="3 3" 
                                        />
                                    </BarChart>
                                )}
                            </ResponsiveContainer>
                        ) : (
                            <Box textAlign="center" py={8}>
                                <Typography variant="h6" color="text.secondary">
                                    No data available for the selected period
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Fade>

                {/* Yearly Breakdown Table */}
                {viewMode === 'yearly' && displayData.length > 0 && (
                    <Fade in timeout={1400}>
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                p: 4,
                                mt: 4,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                            }}
                        >
                            <Typography variant="h5" fontWeight={600} mb={3}>
                                Year-by-Year Breakdown
                            </Typography>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Year</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>Total Deposits</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>Total Expenses</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>Profit/Loss</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '1rem' }}>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {displayData.map((row, index) => (
                                            <TableRow 
                                                key={row.year}
                                                sx={{ 
                                                    '&:hover': { backgroundColor: '#f8f9fa' },
                                                    transition: 'background-color 0.2s'
                                                }}
                                            >
                                                <TableCell sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                                    {row.year}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#11998e', fontWeight: 600 }}>
                                                    ${row.totalDeposits.toFixed(2)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#ee0979', fontWeight: 600 }}>
                                                    ${row.totalExpenses.toFixed(2)}
                                                </TableCell>
                                                <TableCell 
                                                    align="right" 
                                                    sx={{ 
                                                        fontWeight: 700,
                                                        fontSize: '1.1rem',
                                                        color: row.profitOrLoss >= 0 ? '#11998e' : '#ee0979'
                                                    }}
                                                >
                                                    ${row.profitOrLoss.toFixed(2)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        icon={row.profitOrLoss >= 0 ? 
                                                            <TrendingUp /> : 
                                                            <TrendingDown />
                                                        }
                                                        label={row.profitOrLoss >= 0 ? 'Profit' : 'Loss'}
                                                        sx={{
                                                            background: row.profitOrLoss >= 0 ?
                                                                'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' :
                                                                'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
                                                            color: 'white',
                                                            fontWeight: 600
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow sx={{ backgroundColor: '#667eea' }}>
                                            <TableCell sx={{ fontWeight: 700, color: 'white', fontSize: '1.1rem' }}>
                                                TOTAL
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, color: 'white' }}>
                                                ${displayData.reduce((sum, row) => sum + row.totalDeposits, 0).toFixed(2)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, color: 'white' }}>
                                                ${displayData.reduce((sum, row) => sum + row.totalExpenses, 0).toFixed(2)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, color: 'white', fontSize: '1.1rem' }}>
                                                ${displayData.reduce((sum, row) => sum + row.profitOrLoss, 0).toFixed(2)}
                                            </TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Fade>
                )}

                {/* Month-by-Month Breakdown Table - always show when we have monthly data */}
                {getFilteredMonthlyData().length > 0 && (
                    <Fade in timeout={1400}>
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                p: 4,
                                mt: 4,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                            }}
                        >
                            <Typography variant="h5" fontWeight={600} mb={3}>
                                Month-by-Month Details
                            </Typography>
                            <TableContainer sx={{ maxHeight: 600 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '1rem', backgroundColor: '#f5f7fa' }}>Month</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', backgroundColor: '#f5f7fa' }}>Deposits</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', backgroundColor: '#f5f7fa' }}>Expenses</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', backgroundColor: '#f5f7fa' }}>Profit/Loss</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '1rem', backgroundColor: '#f5f7fa' }}>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {getFilteredMonthlyData().map((row, index) => (
                                            <TableRow 
                                                key={index}
                                                sx={{ 
                                                    '&:hover': { backgroundColor: '#f8f9fa' },
                                                    transition: 'background-color 0.2s'
                                                }}
                                            >
                                                <TableCell sx={{ fontWeight: 600 }}>
                                                    {row.month}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#11998e', fontWeight: 600 }}>
                                                    ${row.totalDeposits?.toFixed(2) || '0.00'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#ee0979', fontWeight: 600 }}>
                                                    ${row.totalExpenses?.toFixed(2) || '0.00'}
                                                </TableCell>
                                                <TableCell 
                                                    align="right" 
                                                    sx={{ 
                                                        fontWeight: 700,
                                                        color: row.profitOrLoss >= 0 ? '#11998e' : '#ee0979'
                                                    }}
                                                >
                                                    ${row.profitOrLoss.toFixed(2)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        icon={row.profitOrLoss >= 0 ? 
                                                            <TrendingUp /> : 
                                                            <TrendingDown />
                                                        }
                                                        label={row.profitOrLoss >= 0 ? 'Profit' : 'Loss'}
                                                        size="small"
                                                        sx={{
                                                            background: row.profitOrLoss >= 0 ?
                                                                'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' :
                                                                'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
                                                            color: 'white',
                                                            fontWeight: 600
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Fade>
                )}
                    </>
                )}
            </Container>
        </Box>
    );
}

export default ProfitLossChart;
